import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Database, Download, Upload, Trash2, RefreshCw, Clock, 
  CheckCircle, AlertTriangle, Archive
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BackupManager = ({ token }) => {
  const [backups, setBackups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/backup/list?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error loading backups:', error);
      toast.error('Fehler beim Laden der Backups');
    } finally {
      setIsLoading(false);
    }
  };

  const createBackup = async () => {
    setIsCreating(true);
    try {
      const response = await axios.post(
        `${API}/admin/backup/create`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        `Backup erstellt! ${response.data.counts.translations} Übersetzungen, ` +
        `${response.data.counts.destinations} Destinationen gesichert`
      );
      
      await loadBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Fehler beim Erstellen des Backups');
    } finally {
      setIsCreating(false);
    }
  };

  const restoreBackup = async (backupId, timestamp) => {
    if (!window.confirm(
      `Wirklich von diesem Backup wiederherstellen?\n\n` +
      `Zeitpunkt: ${new Date(timestamp).toLocaleString('de-DE')}\n\n` +
      `⚠️ WARNUNG: Alle aktuellen Daten werden überschrieben!`
    )) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/backup/${backupId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        `Backup wiederhergestellt! ` +
        `${Object.values(response.data.restored_counts).reduce((a,b) => a+b, 0)} Einträge wiederhergestellt`
      );
      
      // Reload page to reflect changes
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm('Backup wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/admin/backup/${backupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Backup gelöscht');
      await loadBackups();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBackupAge = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    if (diffHours > 0) return `vor ${diffHours} Stunde${diffHours > 1 ? 'n' : ''}`;
    return 'gerade eben';
  };

  if (isLoading) {
    return (
      <div className="backup-loading">
        <RefreshCw className="spin" size={48} />
        <p>Lade Backups...</p>
      </div>
    );
  }

  return (
    <div className="backup-manager">
      <div className="backup-header">
        <div>
          <h2>Backup & Wiederherstellung</h2>
          <p>Sichere und stelle deine Daten wieder her</p>
        </div>
        <button 
          className="btn-primary"
          onClick={createBackup}
          disabled={isCreating}
        >
          {isCreating ? (
            <>
              <RefreshCw className="spin" size={20} />
              Erstelle Backup...
            </>
          ) : (
            <>
              <Database size={20} />
              Neues Backup erstellen
            </>
          )}
        </button>
      </div>

      <div className="backup-info-cards">
        <div className="info-card">
          <Database size={32} color="#6366F1" />
          <div>
            <h4>Backup-Umfang</h4>
            <p>Übersetzungen, Destinationen, Content, Algorithmus, Videos</p>
          </div>
        </div>
        
        <div className="info-card">
          <Clock size={32} color="#10B981" />
          <div>
            <h4>Automatisch</h4>
            <p>Manuelle Backups ergänzen automatische Sicherungen</p>
          </div>
        </div>

        <div className="info-card">
          <AlertTriangle size={32} color="#F59E0B" />
          <div>
            <h4>Vorsicht</h4>
            <p>Wiederherstellung überschreibt aktuelle Daten</p>
          </div>
        </div>
      </div>

      {backups.length === 0 ? (
        <div className="backup-empty">
          <Archive size={64} color="#9CA3AF" />
          <h3>Keine Backups vorhanden</h3>
          <p>Erstelle dein erstes Backup mit dem Button oben</p>
        </div>
      ) : (
        <div className="backup-list">
          <h3>Verfügbare Backups ({backups.length})</h3>
          
          <div className="backup-items">
            {backups.map((backup) => (
              <div key={backup.id} className="backup-item">
                <div className="backup-item-header">
                  <div className="backup-item-info">
                    <div className="backup-icon">
                      <Database size={24} />
                    </div>
                    <div>
                      <h4>{formatDate(backup.created_at)}</h4>
                      <p className="backup-age">{getBackupAge(backup.created_at)}</p>
                    </div>
                  </div>
                  
                  <div className="backup-item-actions">
                    <button
                      className="btn-icon btn-success"
                      onClick={() => restoreBackup(backup.id, backup.created_at)}
                      title="Wiederherstellen"
                    >
                      <Upload size={18} />
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => deleteBackup(backup.id)}
                      title="Löschen"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <div className="backup-item-stats">
                  {backup.counts && Object.entries(backup.counts).map(([key, count]) => (
                    <div key={key} className="backup-stat">
                      <CheckCircle size={14} />
                      <span>{count}</span>
                      <span className="backup-stat-label">
                        {key === 'translations' ? 'Übersetzungen' :
                         key === 'destinations' ? 'Destinationen' :
                         key === 'content_settings' ? 'Content' :
                         key === 'algorithm_settings' ? 'Algorithmus' :
                         key === 'videos' ? 'Videos' : key}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="backup-tips">
        <h4>💡 Backup-Tipps</h4>
        <ul>
          <li>Erstelle regelmäßig manuelle Backups vor größeren Änderungen</li>
          <li>Backups enthalten alle wichtigen Daten außer User-Submissions</li>
          <li>Wiederherstellung dauert wenige Sekunden</li>
          <li>Alte Backups können gelöscht werden um Speicher zu sparen</li>
        </ul>
      </div>
    </div>
  );
};

export default BackupManager;
